<template>
  <div class="smsConv">
    <!-- header: consumer name + refresh -->
    <div v-if="showHeader" class="smsConv__head">
      <div class="smsConv__name" :title="consumerName">
        {{ consumerName || "Conversation" }}
      </div>
      <button type="button" class="smsConv__refresh" title="Refresh" aria-label="Refresh" :disabled="refreshing" @click="refresh">
        <i class="fa fa-refresh" :class="{ 'fa-spin': refreshing }" aria-hidden="true" />
      </button>
    </div>

    <!-- thread: oldest at top, newest at bottom -->
    <div ref="threadEl" class="smsConv__thread" @scroll="onScroll">
      <div v-if="loadingOlder" class="smsConv__inlineSpin">
        <span class="smsConv__spinner" />
      </div>

      <div v-if="loading && !messages.length" class="smsConv__state">
        <span class="smsConv__spinner" />
      </div>
      <div v-else-if="error && !messages.length" class="smsConv__state smsConv__state--error">
        {{ error }}
      </div>
      <EmptyState v-else-if="!messages.length" title="No messages yet" text="" min-height="min-h-[200px]" />

      <template v-for="row in rows" :key="row.key">
        <div v-if="row.type === 'day'" class="smsConv__day">
          <span>{{ row.label }}</span>
        </div>
        <div
          v-else
          class="smsConv__row"
          :class="[row.m.is_incoming ? 'smsConv__row--in' : 'smsConv__row--out', { 'smsConv__row--switch': row.sideChanged }]"
        >
          <div class="smsConv__bubble" :class="bubbleClass(row.m)">
            <div class="smsConv__sender">
              {{ senderName(row.m) }}
            </div>
            <!-- one line: no template whitespace around the content (pre-wrap); links become <a>, no v-html -->
            <div class="smsConv__text"><template v-for="(part, i) in $helper.linkParts(cleanText(row.m.content))" :key="i"><a v-if="part.url" :href="part.url" target="_blank" rel="noopener noreferrer" class="smsConv__link">{{ part.text }}</a><template v-else>{{ part.text }}</template></template></div>
            <div class="smsConv__foot">
              <span v-if="row.m.line_label" class="smsConv__line">{{ row.m.line_label }}</span>
              <span class="smsConv__time">{{ formatTime(row.m.sent_at) }}</span>
              <template v-if="!row.m.is_incoming">
                <span v-if="row.m.status === 'failed'" class="smsConv__failed">&#9888; Failed</span>
                <span v-else-if="row.m.status === 'success'" class="smsConv__tick" title="Sent">&#10003;&#10003;</span>
                <!-- pending: clock (same icon as the header notifications) -->
                <div v-else class="smsConv__clock" title="Pending"><svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 36 36" aria-hidden="true"><path d="M0 0h36v36H0z" fill="none" /><path fill="currentColor" d="M18 2a16 16 0 1 0 16 16A16 16 0 0 0 18 2m0 30a14 14 0 1 1 14-14a14 14 0 0 1-14 14" /><path fill="currentColor" d="M18.92 18.4v-7.65a1 1 0 0 0-2 0v8.72l5.9 4a1 1 0 1 0 1.11-1.66Z" /><path fill="currentColor" d="M8 17.94a9.94 9.94 0 0 1 15.41-8.35l.85-1.36a11.55 11.55 0 1 0-8.53 21L16 27.7a10 10 0 0 1-8-9.76" /></svg></div>
              </template>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- inline errors: a failed refresh while messages are shown, or a failed config -->
    <div v-if="error && messages.length" class="smsConv__configError">
      {{ error }}
    </div>
    <div v-if="configError" class="smsConv__configError">
      {{ configError }}
    </div>

    <!-- composer -->
    <div v-if="canSend" class="smsConv__composer">
      <div v-if="fromPhones.length > 1" class="smsConv__from">
        <!-- self-start + 34px line: stays level with the (small) button while the list is open below it -->
        <label class="smsConv__fromLabel self-start leading-[34px]" for="smsConvFrom">From</label>
        <FxSelect v-model="selectedPhoneId" :options="fromPhones" label-key="title" small inline
          wrapper-class="flex-1 min-w-0" @change="userChosePhone = true" />
      </div>
      <div class="smsConv__inputRow">
        <!-- Enter sends, Shift+Enter adds a line; the box grows with the text up to 120px, then scrolls -->
        <textarea
          ref="inputEl"
          v-model="text"
          class="smsConv__input"
          rows="1"
          maxlength="1600"
          placeholder="Type a message"
          autocomplete="off"
          :disabled="sending"
          @keydown.enter.exact.prevent="send"
        />
        <button type="button" class="fxBtnPrimary smsConv__send !rounded-full !min-h-[40px] !h-[40px] !px-[18px]" :disabled="!canSubmit" @click="send">
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#FFFFFF" d="M3.4 20.4 21 12 3.4 3.6 3.4 10l12.6 2-12.6 2z" />
          </svg>
          Send
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import EmptyState from "@/components/partialComponent/EmptyState.vue";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useToast } from "vue-toastification";
import { smsApi } from "@/services/smsApi";
import FxSelect from "@/components/partialComponent/FxSelect.vue";

const props = defineProps({
  companyConsumerId: { type: [Number, String], required: true },
  consumerName: { type: String, default: "" },
  // false inside the SMS window: its modal header shows the name and the refresh button
  showHeader: { type: Boolean, default: true },
});

const toast = useToast();

const threadEl = ref(null);
// raw API order: newest first (desc)
const messages = ref([]);
const page = ref(1);
const totalPages = ref(1);
const loading = ref(false);
const loadingOlder = ref(false);
const refreshing = ref(false);
const error = ref("");

const config = ref(null);
const configError = ref("");
const selectedPhoneId = ref(null);
const userChosePhone = ref(false);

const text = ref("");
const sending = ref(false);
const inputEl = ref(null);

// grow the composer with its lines (also shrinks back after send clears it)
watch(text, () => nextTick(() => {
  const el = inputEl.value;
  if (!el) return;
  el.style.height = "auto";
  el.style.height = Math.min(el.scrollHeight + 2, 120) + "px";
}));

// bumped per consumer so late responses for a previous consumer are dropped
let loadToken = 0;

const fromPhones = computed(() => config.value?.from_phones || []);
const canSend = computed(() => !!config.value?.can_send_sms && fromPhones.value.length > 0);
const canSubmit = computed(() => !sending.value && selectedPhoneId.value != null && text.value.trim().length > 0);

// ---- formatting ----
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const toDate = (sentAt) => {
  const n = Number(sentAt);
  return n ? new Date(n * 1000) : null;
};
const dayKey = (d) => (d ? `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}` : "");
// "EEE MMM dd yyyy"
const formatDay = (d) => (d ? `${DAYS[d.getDay()]} ${MONTHS[d.getMonth()]} ${String(d.getDate()).padStart(2, "0")} ${d.getFullYear()}` : "");
// "h:mm a"
function formatTime(sentAt) {
  const d = toDate(sentAt);
  if (!d) return "";
  const h = d.getHours();
  return `${h % 12 || 12}:${String(d.getMinutes()).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
}
const cleanText = (s) => String(s || "").replace(/\r/g, "");
const senderName = (m) => (m.is_automated ? "System" : m.sender_name || (m.is_incoming ? props.consumerName : ""));
const bubbleClass = (m) => (m.is_automated ? "smsConv__bubble--auto" : m.is_incoming ? "smsConv__bubble--in" : "smsConv__bubble--out");

// display rows: oldest first, a day separator whenever the day changes
const rows = computed(() => {
  const out = [];
  let lastDay = null;
  let lastSide = null;
  [...messages.value].reverse().forEach((m) => {
    const d = toDate(m.sent_at);
    const key = dayKey(d);
    if (key && key !== lastDay) {
      out.push({ type: "day", key: `day-${key}`, label: formatDay(d) });
      lastDay = key;
    }
    const side = m.is_incoming ? "in" : "out";
    out.push({ type: "msg", key: `m-${m.id}`, m, sideChanged: lastSide !== null && side !== lastSide });
    lastSide = side;
  });
  return out;
});

// ---- scrolling ----
function scrollToBottom() {
  nextTick(() => {
    const el = threadEl.value;
    if (el) el.scrollTop = el.scrollHeight;
  });
}

function onScroll() {
  const el = threadEl.value;
  if (el && el.scrollTop < 40) loadOlder();
}

// ---- loading ----
async function loadFirstPage({ silent = true } = {}) {
  const token = loadToken;
  loading.value = true;
  error.value = "";
  const res = await smsApi.conversation(props.companyConsumerId, 1, { silent });
  if (token !== loadToken) return;
  loading.value = false;
  if (!res.ok) {
    error.value = res.message || "Unable to load the conversation.";
    return;
  }
  messages.value = Array.isArray(res.body) ? res.body : [];
  page.value = 1;
  totalPages.value = Number(res.paging?.total_pages) || 1;
  selectDefaultPhone();
  scrollToBottom();
  // a short first page may not overflow the thread, so scrolling up could never trigger "load older"
  nextTick(() => {
    const el = threadEl.value;
    if (el && el.scrollHeight <= el.clientHeight) loadOlder();
  });
}

async function loadOlder() {
  if (loading.value || loadingOlder.value || page.value >= totalPages.value || !messages.value.length) return;
  const token = loadToken;
  const el = threadEl.value;
  loadingOlder.value = true;
  const res = await smsApi.conversation(props.companyConsumerId, page.value + 1, { silent: true });
  if (token !== loadToken) return;
  loadingOlder.value = false;
  if (!res.ok || !Array.isArray(res.body)) return;
  const prevHeight = el ? el.scrollHeight : 0;
  const prevTop = el ? el.scrollTop : 0;
  const seen = new Set(messages.value.map((m) => m.id));
  messages.value = [...messages.value, ...res.body.filter((m) => !seen.has(m.id))];
  page.value += 1;
  totalPages.value = Number(res.paging?.total_pages) || totalPages.value;
  // keep the viewport on the same message after prepending
  await nextTick();
  if (el) el.scrollTop = el.scrollHeight - prevHeight + prevTop;
}

async function loadConfig({ silent = true } = {}) {
  const token = loadToken;
  configError.value = "";
  const res = await smsApi.config({ silent });
  if (token !== loadToken) return;
  if (!res.ok) {
    config.value = null;
    configError.value = res.message || "Unable to load SMS settings.";
    return;
  }
  config.value = res.body || { can_send_sms: false, from_phones: [] };
  // drop the selected line if config no longer has it
  if (selectedPhoneId.value != null && !fromPhones.value.some((p) => p.value === selectedPhoneId.value)) {
    selectedPhoneId.value = null;
    userChosePhone.value = false;
  }
  selectDefaultPhone();
}

// default reply line: the line the consumer last texted into, else the first one
function selectDefaultPhone() {
  const phones = fromPhones.value;
  if (!phones.length || userChosePhone.value) return;
  const lastIncoming = messages.value.find((m) => m.is_incoming);
  let match = null;
  if (lastIncoming) {
    // line_phone_id first (API doc); titles look like "Label · (208) 782-8032", so Android's prefix check rarely matches
    if (lastIncoming.line_phone_id != null) match = phones.find((p) => p.value === Number(lastIncoming.line_phone_id));
    if (!match && lastIncoming.to_phone) match = phones.find((p) => p.title && p.title.startsWith(lastIncoming.to_phone));
  }
  selectedPhoneId.value = (match || phones[0]).value;
}

function resetState() {
  loadToken++;
  messages.value = [];
  page.value = 1;
  totalPages.value = 1;
  error.value = "";
  loading.value = false;
  loadingOlder.value = false;
  config.value = null;
  configError.value = "";
  selectedPhoneId.value = null;
  userChosePhone.value = false;
  text.value = "";
}

function init() {
  resetState();
  if (!props.companyConsumerId) return;
  loadFirstPage();
  loadConfig();
}

// the SMS window header calls refresh()
defineExpose({ refresh: () => refresh() });

async function refresh() {
  if (refreshing.value) return;
  refreshing.value = true;
  await loadConfig({ silent: false });
  await loadFirstPage({ silent: false });
  refreshing.value = false;
}

async function send() {
  if (!canSubmit.value) return;
  const body = text.value.trim();
  sending.value = true;
  const res = await smsApi.send(props.companyConsumerId, selectedPhoneId.value, body);
  sending.value = false;
  if (!res.ok) {
    toast.error(res.message || "Unable to send the SMS, please try again.");
    return;
  }
  text.value = "";
  if (res.message) toast.success(res.message);
  // reload so the sent message shows with its status from the server
  await loadFirstPage({ silent: true });
  scrollToBottom();
}

onMounted(init);
watch(() => props.companyConsumerId, (v, old) => {
  if (String(v) !== String(old)) init();
});
</script>

<style scoped>
.smsConv {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: #fff;
}
.smsConv__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #e1e5ef;
  flex: 0 0 auto;
}
.smsConv__name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* white icon on the project red, same as the SMS window header */
.smsConv__refresh {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 0;
  border-radius: 50%;
  background: #ee5851;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
}
.smsConv__refresh:hover:not(:disabled) {
  background: #d9443d;
}
.smsConv__refresh:disabled {
  opacity: 0.5;
  cursor: default;
}
.smsConv__thread {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  background: #fff;
  padding: 12px 4px 12px 0;
  display: flex;
  flex-direction: column;
}
.smsConv__state {
  margin: auto;
  color: #8a8fa3;
  font-size: 14px;
  text-align: center;
  padding: 24px 12px;
}
.smsConv__state--error {
  color: #d93025;
}
.smsConv__inlineSpin {
  display: flex;
  justify-content: center;
  padding: 4px 0 8px;
}
.smsConv__spinner {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid #c9d3f5;
  border-top-color: #7695ff;
  border-radius: 50%;
  animation: smsConvSpin 0.8s linear infinite;
}
@keyframes smsConvSpin {
  to {
    transform: rotate(360deg);
  }
}
.smsConv__day {
  display: flex;
  justify-content: center;
  margin: 12px 0 7px;
}
.smsConv__day span {
  background: #e3e8f4;
  border-radius: 10px;
  padding: 3px 10px;
  font-size: 11px;
  color: #5b6378;
}
.smsConv__row {
  display: flex;
  margin-top: 5px;
}
.smsConv__row--switch {
  margin-top: 12px;
}
.smsConv__row--in {
  justify-content: flex-start;
}
.smsConv__row--out {
  justify-content: flex-end;
}
.smsConv__bubble {
  max-width: 78%;
  padding: 8px 12px;
  border-radius: 13px;
  word-break: break-word;
}
.smsConv__row--in .smsConv__bubble {
  border-top-left-radius: 3px;
}
.smsConv__row--out .smsConv__bubble {
  border-top-right-radius: 3px;
}
.smsConv__link {
  text-decoration: underline;
  word-break: break-all;
  color: #7695ff;
}
.smsConv__bubble--out .smsConv__link {
  color: #fff;
}
.smsConv__bubble--out {
  background: #7695ff;
  color: #fff;
}
.smsConv__bubble--in {
  background: #fff;
  border: 1px solid #e1e5ef;
  color: #333;
}
.smsConv__bubble--auto {
  background: #fff;
  border: 1px solid #ffd2bd;
  border-left: 3px solid #fe5000;
  color: #333;
}
.smsConv__sender {
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 2px;
}
.smsConv__bubble--out .smsConv__sender {
  color: #ffb48f;
}
.smsConv__bubble--in .smsConv__sender {
  color: #7695ff;
}
.smsConv__bubble--auto .smsConv__sender {
  color: #fe5000;
}
.smsConv__text {
  font-size: 14px;
  line-height: 1.4;
  white-space: pre-wrap;
}
.smsConv__foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
  font-size: 11px;
  opacity: 0.85;
}
.smsConv__bubble--in .smsConv__foot,
.smsConv__bubble--auto .smsConv__foot {
  color: #8a8fa3;
}
.smsConv__clock {
  display: flex;
  align-items: center;
  width: 17px;
  height: 17px;
  color: #999;
  /* 11px digits sit above the middle of their line box: lift the icon to their visual centre */
  transform: translateY(-1px);
}
/* on the solid (blue) bubble the clock is white */
.smsConv__bubble--out .smsConv__clock {
  color: #fff;
}
.smsConv__line {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 180px;
}
.smsConv__tick {
  color: #fe5000;
  font-weight: 700;
  letter-spacing: -2px;
}
.smsConv__failed {
  background: #d93025;
  color: #fff;
  border-radius: 8px;
  padding: 0 6px;
  font-weight: 600;
}
.smsConv__configError {
  flex: 0 0 auto;
  padding: 8px 16px;
  font-size: 13px;
  color: #d93025;
  border-top: 1px solid #e1e5ef;
}
/* same as the chat window composer */
.smsConv__composer {
  flex: 0 0 auto;
  border-top: 1px solid #efefef;
  padding: 15px 0 0;
  margin-top: 10px;
  background: #fff;
}
.smsConv__from {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.smsConv__fromLabel {
  font-size: 12px;
  font-weight: 600;
  color: #5b6378;
  margin: 0;
}
.smsConv__inputRow {
  display: flex;
  align-items: flex-end;
  gap: 10px;
}
.smsConv__input {
  flex: 1 1 auto;
  min-width: 0;
  resize: none;
  border: 1px solid #e1e5ef;
  border-radius: 20px;
  padding: 9px 15px;
  font-size: 14px;
  line-height: 20px;
  min-height: 40px;
  max-height: 120px;
}
.smsConv__input:focus {
  outline: none;
  border-color: #00b7c2;
}
.smsConv__send:disabled {
  opacity: 0.45;
  cursor: default;
}
</style>
